import {
  moveCheckedTo,
  setCheckedFavoriteState,
  toggleFavorite,
} from './legacyBridge/legacyActions'
import { dispatchDeskNavigation, getDeskNavigationSequence } from './deskNavigation'
import { getMsgDeskRuntime } from './runtime/MsgDeskRuntime'
import { treeStore, selectAccessGrant } from './state'
import {
  legacyPagingModeForContext,
  legacyMoveSourceForContext,
  resolveItemAction,
  resolveItemFavoriteAction,
  type ItemActionContext,
  type ItemActionId,
  type ItemFavoriteActionId,
  type ItemSection,
} from './contracts/itemMovements'

export * from './contracts/itemMovements'

export type CheckedItemActionRequest = {
  scope?: 'checked'
  action: ItemActionId
  context: ItemActionContext
  section: ItemSection | 0 | 1
  target: unknown
  reason?: string
}

export type IndividualItemActionRequest = {
  scope: 'item'
  action: ItemFavoriteActionId
  elId: number
  isFavorite: boolean
  target: unknown
  context?: ItemActionContext
}

export type ItemActionRequest = CheckedItemActionRequest | IndividualItemActionRequest

async function preserveFavoritesWorkspace(target: unknown, expectedNavigationSequence: number): Promise<void> {
  await getMsgDeskRuntime().facade.appMsgObj?.legacy?.whenIdle?.('itemMovements:preserve-favorites')
  if (getDeskNavigationSequence() !== expectedNavigationSequence) return
  dispatchDeskNavigation({ mode: 'favs', target })
}

// FAVS-COUNT: fav toggles and moves mutate the live count_subs inside legacy's
// ASYNC ajax callback — after the synchronous post-event projection has already
// run. Wait for the operation to settle, then re-project chrome so the tab
// counts (read from the live count_subs array) refresh without a full reload.
async function syncCountsAfterIdle(reason: string): Promise<void> {
  await getMsgDeskRuntime().facade.appMsgObj?.legacy?.whenIdle?.(reason)
  treeStore.mutate.projectChromeState?.()
}

export async function dispatchItemAction(request: ItemActionRequest): Promise<unknown> {
  if (request.scope === 'item') {
    const action = resolveItemFavoriteAction(request.action)
    if (request.isFavorite === action.isMember) return false
    const navigationSequence = getDeskNavigationSequence()
    // FAVS-VISITOR: on an edit-locked desk a logged visitor's star must still
    // toggle — into their OWN favorites bucket, server-side. The legacy
    // _toggle_fav bails on the edit-lock guard (no request is ever sent), so
    // tell it to proceed for the pure-visitor case (logged but cannot edit).
    // Owners are unaffected: an edit-unlocked / pin-unlocked desk clears the
    // guard on its own and forceVisitorFav is false.
    const grant = selectAccessGrant(treeStore.getState())
    const forceVisitorFav = !!grant?.viewer?.is_logged && !grant?.capabilities?.can_edit
    const result = toggleFavorite(
      request.target,
      request.elId,
      forceVisitorFav,
      request.context === 'favorites' ? 'favs' : undefined,
    )
    if (!action.isMember && treeStore.getState().renderProjection.mode === 'favs') {
      await preserveFavoritesWorkspace(request.target, navigationSequence)
    }
    await syncCountsAfterIdle('itemMovements:fav-toggle-count')
    return result
  }

  return dispatchCheckedItemAction(request)
}

export async function dispatchCheckedItemAction(request: CheckedItemActionRequest): Promise<unknown> {
  const action = resolveItemAction(request.context, request.action)
  const section = request.section === 'submsges' || request.section === 1 ? 1 : 0

  if (action.kind === 'membership') {
    const navigationSequence = getDeskNavigationSequence()
    const result = await setCheckedFavoriteState(
      request.target,
      section,
      legacyPagingModeForContext(request.context),
      action.isMember,
    )
    if (request.context === 'favorites') await preserveFavoritesWorkspace(request.target, navigationSequence)
    await syncCountsAfterIdle('itemMovements:membership-count')
    return result
  }

  const result = moveCheckedTo(request.target, section, action.legacyDestination, {
    mode: legacyMoveSourceForContext(request.context) || undefined,
    reason: request.reason || `itemAction:${request.action}`,
  })
  // FAVS-COUNT: a move shifts the source/destination folder counts (legacy
  // updates count_subs in place); refresh the tab counts once it settles.
  await syncCountsAfterIdle('itemMovements:move-count')
  return result
}
